import { NOTIFICATION_CATEGORY_ICONS } from '@views/settings/Notifications/notificationCategories'
import type { ActivityType, NotificationCategory } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import type { ComponentType } from 'react'

export type PanelCategory = Extract<
  NotificationCategory,
  'SECURITY' | 'ONBOARDING' | 'ACCOUNT' | 'CARDS' | 'INVOICING_EXPENSES' | 'SAVINGS' | 'MARKETING_NEWS'
>

const CATEGORY_FALLBACK_NAMES: Record<PanelCategory, string> = {
  SECURITY: 'Security',
  ONBOARDING: 'Onboarding & Sign-up',
  ACCOUNT: 'Account',
  CARDS: 'Cards',
  INVOICING_EXPENSES: 'Invoicing & Expenses',
  SAVINGS: 'Savings',
  MARKETING_NEWS: 'Marketing & News'
}

// Anything the BE sends that is not listed here (e.g. push campaigns) is shown as Marketing & News.
const ACTIVITY_CATEGORIES: Partial<Record<ActivityType, PanelCategory>> = {
  REGISTERED_ACCOUNT: 'ONBOARDING',
  ACCOUNT_APPROVED: 'ONBOARDING',
  KYB_DOCUMENT_EXPIRING: 'ONBOARDING',

  DORMANT_ACCOUNT: 'ACCOUNT',
  CUSTOMER_CREATED: 'ACCOUNT',
  CONTACT_CREATED: 'ACCOUNT',
  ISSUE_IBAN_INITIATED: 'ACCOUNT',
  ISSUE_IBAN_ISSUED: 'ACCOUNT',
  ISSUE_IBAN_FAILED: 'ACCOUNT',
  ISSUE_IBAN_DECLINED: 'ACCOUNT',

  INVOICE_CREATED: 'INVOICING_EXPENSES',
  INVOICE_OPENED: 'INVOICING_EXPENSES',
  INVOICE_PAID: 'INVOICING_EXPENSES',
  INVOICE_PARTIALLY_PAID: 'INVOICING_EXPENSES',
  ENOTAS_INVOICE_APPROVED: 'INVOICING_EXPENSES',
  ENOTAS_INVOICE_CANCELED: 'INVOICING_EXPENSES',
  ENOTAS_INVOICE_DECLINED: 'INVOICING_EXPENSES',

  SAVING_OPENED: 'SAVINGS',
  SAVING_CANCELLED: 'SAVINGS',
  SAVING_MATURED: 'SAVINGS',
  SAVING_PAID_OUT: 'SAVINGS',
  SAVING_RENEWED: 'SAVINGS',
  SAVING_EARLY_EXIT_REQUESTED: 'SAVINGS',
  SAVING_RATE_CHANGE: 'SAVINGS',
  OVERDRAFT_MONTHLY_STATEMENT_AVAILABLE: 'SAVINGS',
  OVERDRAFT_REPAYMENT_REMINDER: 'SAVINGS',
  OVERDRAFT_LIMIT_REACHED: 'SAVINGS',
  OVERDRAFT_AUTO_FROZEN: 'SAVINGS',
  OVERDRAFT_FROZEN_BY_ADMIN: 'SAVINGS',
  OVERDRAFT_UNFROZEN_BY_ADMIN: 'SAVINGS',
  OVERDRAFT_APPROVED: 'SAVINGS',
  OVERDRAFT_REJECTED: 'SAVINGS',
  OVERDRAFT_SURPLUS_ADDED: 'SAVINGS',
  OVERDRAFT_FEE_CHANGED: 'SAVINGS',

  CARD_TERMINATED: 'CARDS',
  CARD_SUSPENDED: 'CARDS',
  CARD_FROZEN: 'CARDS',

  PASSKEY_ENROLLED: 'SECURITY',
  PASSKEY_REVOKED: 'SECURITY'
}

export const getActivityCategory = (type?: ActivityType): PanelCategory =>
  (type && ACTIVITY_CATEGORIES[type]) || 'MARKETING_NEWS'

export const getCategoryName = (category: PanelCategory): string =>
  tr(`FRONTEND.NOTIFICATIONS.CATEGORY.${category}`, CATEGORY_FALLBACK_NAMES[category])

export const getCategoryIcon = (category: PanelCategory): ComponentType => NOTIFICATION_CATEGORY_ICONS[category]
